import mediaScreen from "core/checkMedia.core";
import transitionReset from "core/transitionReset.core"
import UI from "./elements.module";


class NavigationModule {

	// ===== Element handles =====
	private ui = new UI()
	private overlay: HTMLElement | null;
	private dropdownResizeObserver?: ResizeObserver;


	private static readonly TRANSITION_CLASS = "animate-height";

	constructor() {
		// headerObserver.module.ts injects an element before <header>
		this.overlay = document.querySelector("header")?.previousElementSibling as HTMLElement;
		this.bindEvents();
	}

	/* ──────────────────────────── PRIVATE HELPERS ──────────────────────────── */

	private get currentPage() {
		return window.location.pathname;
	}

	private getHrefAttribute(element: HTMLElement) {
		return element.querySelector("a")?.getAttribute("href") as string
	}

	private setActiveLink(listItem: HTMLElement, classList: string) {
		const hasDropdown = listItem.closest(".dropdown");

		if (this.getHrefAttribute(listItem) === this.currentPage) {
			listItem.classList.add(classList);

			/* highlight parent <li> when inside .navBarListItem */
			listItem.closest(".navBarListItem")?.classList.add(classList);

			/* keep dropdown parent active when its child is active */
			if (listItem.classList.contains("active") && hasDropdown) {
				hasDropdown.closest("li")?.classList.add("active");
			} else {
				hasDropdown?.closest("li")?.classList.remove("active");
			}
		}
	}

	private updateNavRealHeight(): void {
		const nav = this.ui.navBar as HTMLElement;
		nav.style.setProperty(
			"--nav-real-height",
			`${Math.min(nav.scrollHeight, window.innerHeight)}px`
		);
	}

	private getDropdownResizeObserver(): ResizeObserver {
		if (!this.dropdownResizeObserver) {
			this.dropdownResizeObserver = new ResizeObserver(entries => {
				entries.forEach(entry => {
					const target = entry.target as HTMLElement;
					if (target.hasAttribute("data-nav-dropdown")) {
						const max = Math.min(target.scrollHeight, window.innerHeight);
						target.style.setProperty("--dd-real-height", `${max}px`);

						this.updateNavRealHeight()
					}
				});
			});
		}
		return this.dropdownResizeObserver;
	}

	private bindEvents() {
		/* menu button */
		this.ui.navBarToggle?.addEventListener("click", () => this.toggleMobileNav());

		/* backdrop — one‑shot listener */
		this.overlay?.addEventListener("click", () => this.closeMobileNav(), {
			passive: true,
		});
	}

	private toggleMobileNav() {
		const isOpen = this.ui.navBar?.hasAttribute("data-mobile-nav");
		isOpen ? this.closeMobileNav() : this.openMobileNav();
	}

	private openMobileNav() {

		const nav = this.ui.navBar as HTMLElement;
		const realH = Math.min(nav.scrollHeight, window.innerHeight);
		nav.style.setProperty("--nav-real-height", `${realH}px`);

		nav.classList.add(NavigationModule.TRANSITION_CLASS);
		nav.setAttribute("data-mobile-nav", "");

		nav.addEventListener(
			"transitionend",
			() => nav.classList.remove(NavigationModule.TRANSITION_CLASS),
			{ once: true }
		);


		document.body.setAttribute("data-overlay", "");
		this.ui.navBarToggle?.setAttribute("aria-expanded", "true");
		this.overlay?.setAttribute("has-overlay", "true");
	}

	private closeMobileNav() {
		const nav = this.ui.navBar as HTMLElement;

		nav.classList.add(NavigationModule.TRANSITION_CLASS);

		nav.style.setProperty("--nav-real-height", "0px");
		nav.removeAttribute("data-mobile-nav");

		nav.addEventListener(
			"transitionend",
			() => nav.classList.remove(NavigationModule.TRANSITION_CLASS),
			{ once: true }
		);

		document.body.removeAttribute("data-overlay");
		this.ui.navBarToggle?.setAttribute("aria-expanded", "false");
		this.overlay?.removeAttribute("has-overlay");
		this.resetDropdowns();
	}

	private resetDropdowns() {
		this.ui.navBarDropdown?.forEach((dropdown) => {
			dropdown?.removeAttribute("data-nav-dropdown")
			dropdown?.removeAttribute("style")
		});
	}

	private resetAttributes() {
		this.ui.navBar?.removeAttribute("data-mobile-nav");
		this.ui.navBar?.removeAttribute("style");
		this.ui.navBarToggle?.setAttribute("aria-expanded", "false");
		this.ui.navBarLists?.removeAttribute("aria-expanded");
		document.body.removeAttribute("data-overlay");
		this.overlay?.removeAttribute("has-overlay");
		this.resetDropdowns();
	}

	/* ──────────────────────────── PUBLIC API ──────────────────────────── */

	disableAttributes() {
		transitionReset("[data-navbar]", ".main-nav", ".dropdown")

		mediaScreen('min-width', '768', (e: MediaQueryListEvent) => {
			if (e.matches) {
				this.resetAttributes()
			} else {
				this.ui.navBarLists?.removeAttribute('aria-expanded')
				document.body.removeAttribute('data-overlay')
			}
		})

	}

	handleActiveLinkOnPageLoad(): void {
		let linkItem: HTMLAnchorElement | null = null
		let linkHref: string | null = null

		this.ui.navBarList?.forEach(listItem => {
			this.setActiveLink(listItem, "active");
			if (listItem.classList.contains("active")) {
				linkItem = listItem?.querySelector("a") as HTMLAnchorElement
				linkHref = this.getHrefAttribute(listItem)
			}

		})
	}

	handleNavBarDropdown(): void {
		this.ui.navBarDropdown?.forEach((dropdown) => {
			const parentLi = dropdown.closest("li");
			const triggerDiv = parentLi?.querySelector(".is-dropdown") as HTMLElement | null;


			parentLi?.addEventListener("click", (e) => {

				if (window.matchMedia("(min-width: 768px)").matches) {
					triggerDiv?.blur();                // gets rid of sticky focus
					// e.preventDefault();
					return;                         // let :hover do the work
				}

				const willopen = !dropdown?.hasAttribute("data-nav-dropdown");

				if (willopen) {
					// 1. Set dropdown to final target height
					const dropdownHeight = Math.min(dropdown.scrollHeight, window.innerHeight);
					dropdown.style.setProperty("--dd-real-height", `${dropdownHeight}px`);
					dropdown.setAttribute("data-nav-dropdown", "");

					// 2. Predict the combined height of the entire nav
					const nav = this.ui.navBar as HTMLElement;
					const currentNavHeight = nav.getBoundingClientRect().height;
					
					// Force the nav to include the new dropdown height for a split second to measure
					const totalNavHeight = Math.min(nav.scrollHeight, window.innerHeight);
					nav.style.setProperty("--nav-real-height", `${totalNavHeight}px`);

					// 3. Start animations simultaneously
					nav.classList.add(NavigationModule.TRANSITION_CLASS);
					dropdown.classList.add(NavigationModule.TRANSITION_CLASS);

					this.getDropdownResizeObserver().observe(dropdown as HTMLElement);

					// Cleanup transition classes
					const onTransitionEnd = () => {
						nav.classList.remove(NavigationModule.TRANSITION_CLASS);
						dropdown.classList.remove(NavigationModule.TRANSITION_CLASS);
					};
					nav.addEventListener("transitionend", onTransitionEnd, { once: true });
				} else {
					const nav = this.ui.navBar as HTMLElement;
					
					nav.classList.add(NavigationModule.TRANSITION_CLASS);
					dropdown.classList.add(NavigationModule.TRANSITION_CLASS);

					dropdown.removeAttribute("data-nav-dropdown");
					
					// Recalculate parent height immediately for closing
					const totalNavHeight = Math.min(nav.scrollHeight - dropdown.scrollHeight, window.innerHeight);
					nav.style.setProperty("--nav-real-height", `${totalNavHeight}px`);

					this.dropdownResizeObserver?.unobserve(dropdown as HTMLElement);

					const onTransitionEnd = () => {
						nav.classList.remove(NavigationModule.TRANSITION_CLASS);
						dropdown.classList.remove(NavigationModule.TRANSITION_CLASS);
						this.updateNavRealHeight(); // Final re-sync
					};
					nav.addEventListener("transitionend", onTransitionEnd, { once: true });
				}

				mediaScreen("min-width", "768", (e: MediaQueryListEvent) => {
					if (e.matches && willopen) {
						dropdown?.removeAttribute("data-nav-dropdown");
					}
				});
			});
		});
	}

	handleNavigationOverflow(): void {
		let height = 413;
		mediaScreen("max-width", "991", (e: MediaQueryListEvent) => {
			const navObserver = new ResizeObserver((entries) => {
				if (e.matches) {
					entries.forEach((entry) => {
						const target = this.ui.navBar as HTMLElement;
						const content = entry.contentRect.height;
						const targetHeight = target.getBoundingClientRect().height;

						if (content <= 0) {
							target.removeAttribute("style");
						}
						if (content > 0 || (content <= 0 && targetHeight > height)) {
							target.style.overflowY = "scroll";
							target.style.height = `${height}px`;
						}
					});
				}
			});

			this.ui.navBarDropdown?.forEach((dropdown) => {
				navObserver.observe(dropdown as HTMLElement);
			});
		});
	}
}

export default NavigationModule
